/* eslint-disable react/jsx-no-undef */
import { cn } from '@form-factory/chatbot-ui';
import type { EfGroupClass } from '@form-factory/schema/contracts';
import { DateFormat, formatUtils } from '@form-factory/shared';
import { Trans } from '@lingui/react/macro';
import { PersonStandingIcon, UsersIcon } from 'lucide-react';

import { useRouter } from '@/hooks/useRouter';

interface ClassCardProps {
  classData: EfGroupClass;
}

export function ClassCard({ classData }: ClassCardProps) {
  const { pushRoute } = useRouter();

  const {
    name,
    startDate,
    endDate,
    roomName,
    backgroundColor,
    isReplacement,
    instructorPhotoUrl,
    instructorFullName,
    participantsLimit,
    participantsNumber,
  } = classData;

  const startTime = formatUtils.date(startDate, DateFormat.Time);
  const endTime = formatUtils.date(endDate, DateFormat.Time);
  const hasStarted = startDate && new Date(startDate) < new Date();

  // Users can't register for classes that start in less than 48 hours
  const timeUntilStart = startDate
    ? new Date(startDate).getTime() - Date.now()
    : 0;
  const is48HoursBeforeStart = timeUntilStart
    ? timeUntilStart < 48 * 60 * 60 * 1000
    : false;

  return (
    <button
      type='button'
      onClick={() => pushRoute('groupClassDetail', classData)}
      className={cn(
        'mb-2 flex w-full flex-col items-start justify-start gap-1 rounded-md border-l-4 bg-chat-background-secondary px-3 py-2',
        hasStarted && 'opacity-50',
      )}
      style={{
        borderColor: backgroundColor ?? '#f9fafb',
      }}
    >
      <div className='flex items-center gap-2'>
        <span className='text-sm font-medium'>
          {startTime} - {endTime}
        </span>
        {isReplacement && (
          <span className='h-4 rounded-full bg-blue-500/20 px-2 text-[10px] font-medium leading-4 text-blue-400'>
            <Trans>Replacement</Trans>
          </span>
        )}
        {is48HoursBeforeStart && timeUntilStart > 0 && (
          <span className='h-4 rounded-full bg-green-500/20 px-2 text-[10px] font-medium leading-4 text-green-400'>
            <Trans>Registration open</Trans>
          </span>
        )}
      </div>
      <div className='flex items-start gap-3'>
        <div className='mt-1'>
          {instructorPhotoUrl ? (
            <img
              src={instructorPhotoUrl}
              alt={instructorFullName}
              className='size-10 rounded-full object-cover'
            />
          ) : (
            <div className='flex size-10 items-center justify-center rounded-full bg-gray-200'>
              <PersonStandingIcon className='size-8 text-gray-500' />
            </div>
          )}
        </div>
        <div className='flex min-w-0 flex-col gap-0.5 text-left'>
          <div className='whitespace-break-spaces break-words text-lg font-bold leading-tight'>
            {name}
          </div>
          <div className='text-sm'>{instructorFullName}</div>
          <div className='flex items-center gap-1 text-xs opacity-70'>
            {roomName && `${roomName} | `}

            <span className='flex items-center gap-1'>
              <UsersIcon className='size-3' />
              {participantsNumber}/{participantsLimit}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}
